import {CancelError, CancelToken} from './asyncCancelHelpers';
import {delayAsync} from './asyncHelpers';

export interface ExponentialBackoffOptions {
  baseDelay: number;
  maxDelay: number;
  onComplete?: (retryCount: number, elapsedTime: number) => void;
}

/** Implements truncated exponential backoff with full jitter. */
// https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
export function withExponentialBackoff<R>(
  callback: (retryCount: number) => R | Promise<R>,
  shouldRetryForError: (error: any) => boolean,
  options: ExponentialBackoffOptions,
  cancelToken?: CancelToken,
): Promise<R> {
  const startTime = performance.now();
  const {baseDelay, maxDelay, onComplete} = options;

  if (baseDelay <= 0) {
    throw new Error('Exponential backoff base delay should be greater than 0');
  }

  if (maxDelay < baseDelay) {
    throw new Error('Exponential backoff maximum delay should be greater than or equal to base delay');
  }

  return new Promise((resolve, reject) => {
    const tryCallback = async (retryCount: number) => {
      try {
        // Calculate the exponential backoff delay.
        const unjitteredDelay = Math.min(baseDelay * 2 ** retryCount, maxDelay);

        // Add full jitter.
        const jitteredDelay = unjitteredDelay * Math.random();

        // Back off.
        await delayAsync(jitteredDelay, cancelToken);

        // Perform the task.
        const result = await callback(retryCount);
        resolve(result);

        // Report.
        if (onComplete) {
          onComplete(retryCount, performance.now() - startTime);
        }
      } catch (error) {
        const isCancelError = error instanceof CancelError;
        const shouldRetry = !isCancelError && shouldRetryForError(error);

        if (shouldRetry) {
          tryCallback(retryCount + 1);
        } else {
          reject(error);
        }
      }
    };

    tryCallback(0);
  });
}
